import React, { useState } from 'react';
import { BarChart2, ListChecks, FileText, Database, GitBranch, Bell, Network, Menu, X, Map, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const MAIN_TABS = [
    { id: 'grafico', label: 'Dashboard', icon: BarChart2, allowedRoles: ['administrador', 'comercial'] },
    { id: 'cobertura', label: 'Cobertura', icon: Map, allowedRoles: ['administrador', 'tecnico', 'atendimento', 'comercial'] },
    { id: 'monitoramento', label: 'Monitoramento', icon: Network, allowedRoles: ['administrador', 'tecnico_de_rede', 'tecnico'] },
    { id: 'projetos', label: 'Projetos', icon: FileText },
    { id: 'pendencias', label: 'Pendências', icon: ListChecks },
    { id: 'notificacoes', label: 'Notificações', icon: Bell },
    { id: 'trocas', label: 'Trocas', icon: GitBranch, allowedRoles: ['administrador', 'tecnico', 'tecnico_campo', 'atendimento', 'comercial'] },
    { id: 'dados', label: 'Dados', icon: Database, allowedRoles: ['administrador'] },
    { id: 'sobre', label: 'Sobre', icon: Info, isMobileOnly: true },
];

export const getVisibleMainTabs = (userRole, { includeMobileOnly = false } = {}) => {
    return MAIN_TABS.filter(tab => {
        if (!includeMobileOnly && tab.isMobileOnly) return false;
        if (tab.id === 'projetos' && (userRole === 'tecnico_campo' || (userRole === 'atendimento' || userRole === 'comercial'))) return false;
        if (tab.id === 'pendencias' && (userRole === 'atendimento' || userRole === 'comercial')) return false;
        if (tab.id === 'notificacoes' && (userRole === 'atendimento' || userRole === 'comercial')) return false;
        if (tab.allowedRoles && !tab.allowedRoles.includes(userRole)) return false;
        return true;
    });
};

export const getFirstAvailableMainTab = (userRole) => getVisibleMainTabs(userRole)[0]?.id;

/**
 * @description Componente que renderiza a navegação principal com design Glassmorphism e animações.
 *
 * @param {object} props - As propriedades do componente.
 * @param {string} props.activeTab - O identificador da aba atualmente ativa.
 * @param {function} props.setActiveTab - Função para mudar a aba ativa.
 * @param {string} props.userRole - A role do usuário para controlar a visibilidade dos itens.
 * @param {boolean} props.hasUrgentNotifications - Indica se existem notificações urgentes.
 * @param {function} props.onOpenAbout - Função para abrir o modal "Sobre".
 */
const MainTabs = ({ activeTab, setActiveTab, userRole, hasUrgentNotifications, onOpenAbout }) => {
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleTabClick = (tabId) => {
        if (tabId === 'sobre') {
            if (onOpenAbout) onOpenAbout();
            setMobileMenuOpen(false);
            return;
        }
        setActiveTab(tabId);
        setMobileMenuOpen(false);
    };

    const visibleTabsDesktop = getVisibleMainTabs(userRole);
    const visibleTabsMobile = getVisibleMainTabs(userRole, { includeMobileOnly: true });
    const activeTabData = MAIN_TABS.find(tab => tab.id === activeTab);

    return (
        <nav
            className="relative z-40 w-full backdrop-blur-md bg-white/90 dark:bg-slate-900/80 border-b border-slate-300/60 dark:border-slate-700/50 shadow-sm transition-all duration-300"
            aria-label="Menu principal"
        >
            <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">

                    {/* --- Desktop Navigation --- */}
                    <div className="hidden sm:flex items-center justify-start space-x-1 w-full overflow-x-auto no-scrollbar py-1 px-1" role="tablist" aria-label="Navegação principal">
                        {visibleTabsDesktop.map((tab) => {
                            const isActive = activeTab === tab.id;
                            const Icon = tab.icon;

                            return (
                                <button
                                    key={tab.id}
                                    role="tab"
                                    aria-selected={isActive}
                                    onClick={() => handleTabClick(tab.id)}
                                    className={`relative px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900 ${
                                        isActive
                                            ? 'text-blue-700 dark:text-blue-400'
                                            : 'text-slate-700 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-800/50'
                                    }`}
                                >
                                    {isActive && (
                                        <motion.div
                                            layoutId="activeTabBackground"
                                            className="absolute inset-0 bg-blue-100/60 dark:bg-blue-500/15 rounded-full"
                                            initial={false}
                                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                        />
                                    )}
                                    <span className="relative z-10 flex items-center space-x-2">
                                        <div className="relative">
                                            <Icon className="w-5 h-5" />
                                            {tab.id === 'notificacoes' && hasUrgentNotifications && (
                                                <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                                                    <span className="sr-only">Novas notificações urgentes</span>
                                                </span>
                                            )}
                                        </div>
                                        <span>{tab.label}</span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* --- Mobile Hamburger Button --- */}
                    <div className="sm:hidden flex items-center justify-between w-full">
                         <div className="flex items-center text-lg font-semibold text-slate-800 dark:text-slate-200">
                            {activeTabData ? (
                                <>
                                    <activeTabData.icon className="h-6 w-6 mr-2 text-blue-600 dark:text-blue-400" />
                                    <span>{activeTabData.label}</span>
                                </>
                            ) : (
                                <span>Menu</span>
                            )}
                        </div>
                        <button
                            id="mobile-menu-button"
                            onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
                            className="p-2 rounded-md text-slate-600 dark:text-slate-300 hover:bg-black/5 dark:hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
                            aria-label={isMobileMenuOpen ? "Fechar menu" : "Abrir menu"}
                            aria-expanded={isMobileMenuOpen}
                            aria-controls="mobile-menu-drawer"
                            aria-haspopup="true"
                        >
                            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* --- Mobile Menu Drawer --- */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        id="mobile-menu-drawer"
                        role="menu"
                        aria-orientation="vertical"
                        aria-labelledby="mobile-menu-button"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="sm:hidden absolute left-0 top-full w-full backdrop-blur-xl bg-white/95 dark:bg-slate-900/95 border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg z-30 overflow-hidden"
                    >
                        <div className="flex flex-col space-y-1 p-4">
                            {visibleTabsMobile.map((tab) => {
                                const isActive = activeTab === tab.id;
                                const Icon = tab.icon;

                                return (
                                    <button
                                        key={tab.id}
                                        role="menuitem"
                                        onClick={() => handleTabClick(tab.id)}
                                        className={`flex items-center w-full px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900 ${
                                            isActive
                                                ? 'bg-blue-100/50 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300'
                                                : 'text-slate-600 dark:text-slate-400 hover:bg-black/5 dark:hover:bg-white/5'
                                        }`}
                                    >
                                        <div className="relative mr-3">
                                            <Icon className="h-5 w-5" />
                                            {tab.id === 'notificacoes' && hasUrgentNotifications && (
                                                <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white dark:ring-slate-800" />
                                            )}
                                        </div>
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default MainTabs;
